import { useQuery } from '@tanstack/react-query'
import { api, list, type AuditEvent, type RetentionPolicy, type BillingSnapshot, type CaseDetail, type CaseRow, type DocumentRow, type Engagement, type ExtractionView, type Finding, type Invoice, type Kpis } from './api'

export const qk = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  findings: (f: Record<string, string>) => ['findings', f] as const,
  finding: (id: string) => ['finding', id] as const,
  engagements: ['engagements'] as const,
  documents: ['documents'] as const,
  extraction: (id: string) => ['extraction', id] as const,
  trail: ['trail'] as const,
  billing: ['billing'] as const,
  invoices: ['invoices'] as const,
  retention: ['retention'] as const,
  cases: ['cases'] as const,
  case: (id: string) => ['cases', id] as const,
}

export function useDashboard() {
  return useQuery({ queryKey: qk.dashboard, queryFn: () => api<Kpis>('/v1/dashboard'), staleTime: 15_000 })
}

export function useFindings(filter: Record<string, string>) {
  const qs = new URLSearchParams(Object.entries(filter).filter(([, v]) => v)).toString()
  return useQuery({
    queryKey: qk.findings(filter),
    queryFn: () => list<Finding>(`/v1/findings${qs ? `?${qs}` : ''}`),
    staleTime: 20_000,
  })
}

export function useFinding(id: string | undefined) {
  return useQuery({
    queryKey: qk.finding(id ?? ''),
    queryFn: () => api<Finding>(`/v1/findings/${id}`),
    enabled: Boolean(id),
  })
}

export function useEngagements() {
  return useQuery({ queryKey: qk.engagements, queryFn: () => list<Engagement>('/v1/engagements'), staleTime: 20_000 })
}

export function useDocuments() {
  return useQuery({ queryKey: qk.documents, queryFn: () => list<DocumentRow>('/v1/documents'), staleTime: 30_000 })
}

export function useTrail() {
  return useQuery({ queryKey: qk.trail, queryFn: () => list<AuditEvent>('/v1/audit-trail'), staleTime: 10_000 })
}

export function useExtraction(id: string | null) {
  return useQuery({
    queryKey: qk.extraction(id ?? ''),
    queryFn: () => api<ExtractionView>(`/v1/documents/${id}/extraction`),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'pending' || s === 'running' ? 1500 : false
    },
  })
}

export function useBilling() {
  return useQuery({ queryKey: qk.billing, queryFn: () => api<BillingSnapshot>('/v1/billing'), staleTime: 20_000 })
}

export function useInvoices() {
  return useQuery({ queryKey: qk.invoices, queryFn: () => list<Invoice>('/v1/invoices'), staleTime: 20_000 })
}

export function useCases() {
  return useQuery({ queryKey: qk.cases, queryFn: () => list<CaseRow>('/v1/cases'), staleTime: 15_000 })
}

export function useCase(id: string | undefined) {
  return useQuery({
    queryKey: qk.case(id ?? ''),
    queryFn: () => api<CaseDetail>(`/v1/cases/${id}`),
    enabled: Boolean(id),
  })
}

export function useRetention() {
  return useQuery({ queryKey: qk.retention, queryFn: () => api<RetentionPolicy>('/v1/retention') })
}
